import moment from 'moment';

export const TYPE_OPTIONS = [
    {value: 'reservation', label: 'Rezervace', short: 'Rezervace', className: 'bg-blue-100 text-blue-800'},
    {value: 'contact', label: 'Kontaktní zpráva', short: 'Kontakt', className: 'bg-slate-100 text-slate-800'},
    {value: 'business', label: 'Poptávka firmy', short: 'Firmy', className: 'bg-purple-100 text-purple-800'},
    {value: 'buyback', label: 'Poptávka výkupu', short: 'Výkup', className: 'bg-emerald-100 text-emerald-800'}
];

export const TYPE_MAP = Object.fromEntries(TYPE_OPTIONS.map((item) => [item.value, item]));

/** Stavy podle typu – musí odpovídat STATUSES_BY_TYPE v API. */
export const STATUSES_BY_TYPE = {
    reservation: ['new', 'confirmed', 'converted', 'no_show', 'cancelled', 'spam'],
    contact: ['new', 'in_progress', 'done', 'spam'],
    business: ['new', 'in_progress', 'offer_sent', 'done', 'rejected', 'spam'],
    buyback: ['new', 'in_progress', 'offer_sent', 'converted', 'rejected', 'spam']
};

const STATUS_LABELS = {
    new: 'Nový',
    in_progress: 'Řeší se',
    confirmed: 'Potvrzeno',
    offer_sent: 'Nabídka odeslána',
    converted: 'Převzato',
    done: 'Vyřízeno',
    no_show: 'Nedorazil',
    cancelled: 'Zrušeno',
    rejected: 'Odmítnuto',
    spam: 'Spam'
};

export const statusLabel = (status, type) => {
    if (status === 'converted') return type === 'buyback' ? 'Vykoupeno' : 'Převzato';
    if (status === 'new' && type === 'reservation') return 'Čeká na potvrzení';
    return STATUS_LABELS[status] || status || '-';
};

export const STATUS_CLASSES = {
    new: 'bg-amber-100 text-amber-800 ring-1 ring-amber-300',
    in_progress: 'bg-blue-100 text-blue-800',
    confirmed: 'bg-blue-100 text-blue-800',
    offer_sent: 'bg-indigo-100 text-indigo-800',
    converted: 'bg-emerald-100 text-emerald-800',
    done: 'bg-emerald-100 text-emerald-800',
    no_show: 'bg-red-100 text-red-800',
    cancelled: 'bg-gray-200 text-gray-700',
    rejected: 'bg-gray-200 text-gray-700',
    spam: 'bg-gray-200 text-gray-500'
};

/** Skupiny stavů pro filtr v seznamu. */
export const STATUS_FILTERS = [
    {value: 'open', label: 'K vyřízení', statuses: ['new', 'in_progress', 'confirmed', 'offer_sent']},
    {value: 'new', label: 'Nové', statuses: ['new']},
    {value: 'closed', label: 'Vyřízené', statuses: ['converted', 'done']},
    {value: 'lost', label: 'Zrušené a odmítnuté', statuses: ['no_show', 'cancelled', 'rejected']},
    {value: 'spam', label: 'Spam', statuses: ['spam']},
    {value: 'all', label: 'Vše', statuses: null}
];

export const CONDITION_LABELS = {
    nove: 'Nové / nerozbalené',
    zanovni: 'Zánovní',
    pouzivane: 'Používané',
    poskozene: 'Poškozené',
    nefunkcni: 'Nefunkční'
};

const WEEKDAYS = ['ne', 'po', 'út', 'st', 'čt', 'pá', 'so'];
const WEEKDAYS_LONG = ['neděle', 'pondělí', 'úterý', 'středa', 'čtvrtek', 'pátek', 'sobota'];
export const DAY_NAMES = ['', 'Pondělí', 'Úterý', 'Středa', 'Čtvrtek', 'Pátek', 'Sobota', 'Neděle'];

/** „po 19. 10.“ (+ „ v 10:00“) */
export function formatTerm(date, time, {long = false, year = false} = {}) {
    if (!date) return '-';
    const m = moment(date, 'YYYY-MM-DD');
    const weekday = (long ? WEEKDAYS_LONG : WEEKDAYS)[m.day()];
    return `${weekday} ${m.format(year ? 'D. M. YYYY' : 'D. M.')}${time ? ` v ${time}` : ''}`;
}

export const todayIso = () => moment().format('YYYY-MM-DD');

/** Relativní popis termínu: dnes / zítra / za 3 dny / před 2 dny */
export function relativeDay(date) {
    if (!date) return '';
    const diff = moment(date, 'YYYY-MM-DD').startOf('day').diff(moment().startOf('day'), 'days');
    if (diff === 0) return 'dnes';
    if (diff === 1) return 'zítra';
    if (diff === -1) return 'včera';
    if (diff > 1) return `za ${diff} ${diff < 5 ? 'dny' : 'dní'}`;
    return `před ${-diff} dny`;
}

/** Stáří požadavku: „před 5 min“, „před 3 h“, „včera 14:20“, „12. 10.“ */
export function formatAge(value) {
    if (!value) return '-';
    const m = moment(value);
    const minutes = moment().diff(m, 'minutes');
    if (minutes < 1) return 'právě teď';
    if (minutes < 60) return `před ${minutes} min`;
    if (minutes < 60 * 12 && m.isSame(moment(), 'day')) return `před ${Math.floor(minutes / 60)} h`;
    if (m.isSame(moment(), 'day')) return `dnes ${m.format('H:mm')}`;
    if (m.isSame(moment().subtract(1, 'day'), 'day')) return `včera ${m.format('H:mm')}`;
    return m.format(m.isSame(moment(), 'year') ? 'D. M. H:mm' : 'D. M. YYYY');
}

export const formatDateTime = (value) => (value ? moment(value).format('D. M. YYYY H:mm') : '-');

export const formatPrice = (value) => (Number(value) > 0 ? `${Number(value).toLocaleString('cs-CZ')} Kč` : 'po diagnostice');

export function formatMinutes(minutes) {
    const value = Number(minutes) || 0;
    if (value < 60) return `${value} min`;
    const hours = Math.floor(value / 60);
    const rest = value % 60;
    return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

/** Zařízení rezervace – starší záznamy mají jen jedno zařízení v plochých polích. */
export const reservationDevices = (reservation) => (reservation?.devices?.length ? reservation.devices : [reservation || {}]);

export const reservationDeviceName = (item) => item?.deviceName || item?.modelCustom || item?.familyName || '';

export const deviceLabel = (inquiry) => reservationDevices(inquiry?.reservation).map(reservationDeviceName).filter(Boolean).join(', ');

/** Krátké shrnutí „o co jde“ pro seznam. */
export function inquirySummary(inquiry) {
    if (inquiry.type === 'reservation') {
        return reservationDevices(inquiry.reservation)
            .map((item) => {
                const repairs = (item.repairs || []).map((repair) => repair.name).join(', ');
                return [reservationDeviceName(item), repairs || (item.issue ? 'podle popisu závady' : '')].filter(Boolean).join(' – ');
            })
            .filter(Boolean)
            .join('; ');
    }
    if (inquiry.type === 'buyback') {
        return [inquiry.buyback?.device, CONDITION_LABELS[inquiry.buyback?.condition]].filter(Boolean).join(' – ');
    }
    if (inquiry.type === 'business') {
        return [inquiry.business?.devices, inquiry.message].filter(Boolean).join(' – ');
    }
    return [inquiry.subject, inquiry.message].filter(Boolean).join(' – ');
}

export const customerName = (customer) => {
    if (!customer) return '';
    if (customer.type === 'company') return customer.company?.name || '';
    return [customer.person?.name, customer.person?.surname].filter(Boolean).join(' ');
};

export const telHref = (phone) => `tel:${String(phone || '').replace(/[^\d+]/g, '')}`;

export const userName = (user) => (user ? `${user.name || ''} ${user.surname || ''}`.trim() : '');

export const StatusBadge = ({status, type, className = ''}) => (
    <span className={`inline-block whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium ${STATUS_CLASSES[status] || 'bg-gray-100 text-gray-700'} ${className}`}>
        {statusLabel(status, type)}
    </span>
);

export const TypeBadge = ({type, short = true}) => {
    const item = TYPE_MAP[type];
    return (
        <span className={`inline-block whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium ${item?.className || 'bg-gray-100'}`}>
            {short ? item?.short : item?.label}
        </span>
    );
};

/** Chybová hláška z API (errorMessage) bez technického kódu. */
export const apiMessage = (error, fallback = 'Akce se nepovedla.') =>
    error?.response?.data?.errorMessage || error?.response?.data?.message || error?.message || fallback;
